import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import GameBoard from '../components/GameBoard/GameBoard';
import Hud from '../components/Hud/Hud';
import PowerUpBar from '../components/PowerUpBar/PowerUpBar';
import ModalFatoRapido from '../components/ModalFatoRapido/ModalFatoRapido';
import confetti from 'canvas-confetti';
import SoundManager from '../utils/SoundManager';
import StorageManager from '../utils/StorageManager';
import { getStorageImageUrls } from '../utils/storageUtils';
import LoadingSplash from '../components/LoadingSplash/LoadingSplash';
import './GameScreen.css';

const GameScreen = ({ nivelId = 'nivel_1', onNavigate }) => {
  // Estados principais
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [modalData, setModalData] = useState(null);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [timeLeft, setTimeLeft] = useState(120);
  const [isPaused, setIsPaused] = useState(false);
  const [isFlashing, setIsFlashing] = useState(false);
  const [errorLog, setErrorLog] = useState([]);
  const [gameState, setGameState] = useState('loading'); // loading, playing, won, lost
  const [splashFinished, setSplashFinished] = useState(false);
  const [dataReady, setDataReady] = useState(false);
  const [biografias, setBiografias] = useState({});
  const [totalPairs, setTotalPairs] = useState(10);
  const [initialTime, setInitialTime] = useState(120);

  // Estados de cooldown dos poderes
  const [thothCooldown, setThothCooldown] = useState(0);
  const [anansiCooldown, setAnansiCooldown] = useState(0);
  const [grioCooldown, setGrioCooldown] = useState(0);

  // Estados para high score
  const [currentHighScore, setCurrentHighScore] = useState(0);
  const [isBeatingRecord, setIsBeatingRecord] = useState(false);

  // Função para embaralhar array
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Carregar nível e criar tabuleiro
  useEffect(() => {
    const loadGame = async () => {
      try {
        // Buscar dados do nível
        const nivelDoc = await getDoc(doc(db, 'niveis', nivelId));
        
        if (!nivelDoc.exists()) {
          console.error('Nível não encontrado');
          setGameState('error');
          return;
        }

        const nivelData = nivelDoc.data();
        setTotalPairs(nivelData.pares);
        setTimeLeft(nivelData.tempo);
        setInitialTime(nivelData.tempo);

        // Buscar biografias das personalidades
        const biografiasMap = {};
        const cardsArray = [];
        
        // TODAS as imagens devem vir do Firebase Storage (basePath obrigatório)
        // Sem fallback para imagem_url ou outros formatos

        for (const personId of nivelData.personalidades_ids) {
          const bioDoc = await getDoc(doc(db, 'biografias', personId));
          
          if (bioDoc.exists()) {
            const bioData = bioDoc.data();
            biografiasMap[personId] = bioData;

            // Determinar conteúdo da carta de foto
            // SEMPRE usar Firebase Storage quando basePath existir
            let fotoContent = null;
            
            // Verificar se basePath existe (obrigatório para todas as personalidades)
            if (bioData.basePath) {
              const imageUrls = getStorageImageUrls(bioData.basePath);
              if (imageUrls) {
                fotoContent = imageUrls;
              } else {
                console.error(`❌ BasePath inválido para ${personId} (nível: ${nivelId}):`, bioData.basePath);
                console.error(`   Verifique se o basePath está no formato correto: "cards/master/{slug}/{slug}"`);
                continue; // Pular sem fallback
              }
            } else {
              console.error(`❌ basePath não encontrado para ${personId} (nível: ${nivelId})`);
              console.error(`   Adicione o campo 'basePath' no Firestore para este documento.`);
              console.error(`   Formato esperado: "cards/master/{slug-normalizado}/{slug-normalizado}"`);
              continue; // Pular sem fallback
            }

            // Criar par de cartas (foto e nome)
            cardsArray.push({
              id: `${personId}_foto`,
              parId: personId,
              type: 'foto',
              content: fotoContent
            });

            cardsArray.push({
              id: `${personId}_nome`,
              parId: personId,
              type: 'nome',
              content: bioData.nome
            });
          }
        }

        setBiografias(biografiasMap);
        setCards(shuffleArray(cardsArray));
        setDataReady(true);

      } catch (error) {
        console.error('Erro ao carregar jogo:', error);
        setGameState('error');
      }
    };

    loadGame();
  }, [nivelId]);

  // Transição para o jogo assim que os dados estiverem prontos E a splash for concluída/pulada
  useEffect(() => {
    if (splashFinished && dataReady && gameState === 'loading') {
      setGameState('playing');
    }
  }, [splashFinished, dataReady, gameState]);

  // Carregar high score do nível
  useEffect(() => {
    if (nivelId) {
      const levelHighScore = StorageManager.getLevelHighScore(nivelId);
      setCurrentHighScore(levelHighScore);
    }
  }, [nivelId]);

  // Verificar se está batendo recorde
  useEffect(() => {
    if (currentHighScore > 0 && score > currentHighScore) {
      setIsBeatingRecord(true);
    } else {
      setIsBeatingRecord(false);
    }
  }, [score, currentHighScore]);

  // Timer do jogo
  useEffect(() => {
    if (gameState !== 'playing' || isPaused || modalData) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setGameState('lost');
          
          // Tocar som de derrota
          SoundManager.play('defeat');
          
          // Salvar resultado
          const gameResult = {
            score: score,
            won: false,
            pairsFound: matchedPairs.length,
            timeSpent: initialTime
          };
          StorageManager.saveGameResult(nivelId, gameResult);
          
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameState, isPaused, modalData, score, matchedPairs.length, nivelId, initialTime]);

  // Cooldown dos poderes
  useEffect(() => {
    if (gameState !== 'playing') return;

    const cooldownTimer = setInterval(() => {
      setThothCooldown((prev) => Math.max(0, prev - 1));
      setAnansiCooldown((prev) => Math.max(0, prev - 1));
      setGrioCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(cooldownTimer);
  }, [gameState]);

  // Verificar vitória
  useEffect(() => {
    if (matchedPairs.length === totalPairs && gameState === 'playing') {
      setTimeout(() => {
        setGameState('won');
        
        // Celebrar com confete
        celebrateVictory();
        
        // Salvar resultado
        const gameResult = {
          score: score,
          won: true,
          pairsFound: totalPairs,
          timeSpent: initialTime - timeLeft
        };
        StorageManager.saveGameResult(nivelId, gameResult);
      }, 500);
    }
  }, [matchedPairs, totalPairs, gameState, score, initialTime, timeLeft, nivelId]);

  // Lógica de verificação de match
  useEffect(() => {
    if (flippedCards.length === 2) {
      const [card1, card2] = flippedCards;

      if (card1.parId === card2.parId) {
        // ACERTO!
        setMatchedPairs((prev) => [...prev, card1.parId]);
        
        // Calcular pontos com combo
        const comboMultiplier = Math.min(combo + 1, 5);
        const points = 100 * comboMultiplier;
        setScore((prev) => prev + points);
        setCombo(comboMultiplier);

        // Sons
        if (comboMultiplier > 1) {
          SoundManager.play('combo');
        } else {
          SoundManager.play('match');
        }

        // Mostrar modal com fato rápido
        const biografia = biografias[card1.parId];
        if (biografia) {
          setTimeout(() => {
            setModalData(biografia);
          }, 300);
        }

        // Limpar cartas viradas
        setFlippedCards([]);
        
      } else {
        // ERRO!
        setCombo(0);
        setErrorLog((prev) => [...prev, card1.id, card2.id]);
        
        // Som de erro (comentado até arquivo estar disponível)
        // SoundManager.play('error');

        // Virar cartas de volta após 1 segundo
        setTimeout(() => {
          setFlippedCards([]);
        }, 1000);
      }
    }
  }, [flippedCards, biografias, combo]);

  // Handler de clique na carta
  const handleCardClick = (card) => {
    if (gameState !== 'playing') return;
    if (flippedCards.length >= 2) return;
    if (matchedPairs.includes(card.parId)) return;
    if (flippedCards.some(fc => fc.id === card.id)) return;

    // Som de flip
    SoundManager.play('flip');

    setFlippedCards((prev) => [...prev, card]);
  };

  // Poder: Sabedoria de Thoth
  const handleThothPower = useCallback(() => {
    if (thothCooldown > 0 || gameState !== 'playing') return;

    // Pegar os 2 últimos erros ou um par correto aleatório
    let cardsToReveal = [];
    
    if (errorLog.length >= 2) {
      const lastErrors = errorLog.slice(-2);
      cardsToReveal = cards.filter(card => lastErrors.includes(card.id));
    } else {
      // Revelar um par correto aleatório não encontrado
      const unmatchedPairs = Object.keys(biografias).filter(
        parId => !matchedPairs.includes(parId)
      );
      
      if (unmatchedPairs.length > 0) {
        const randomPair = unmatchedPairs[Math.floor(Math.random() * unmatchedPairs.length)];
        cardsToReveal = cards.filter(card => card.parId === randomPair);
      }
    }

    if (cardsToReveal.length > 0) {
      setFlippedCards(cardsToReveal);
      setTimeout(() => {
        setFlippedCards([]);
      }, 3000);
    }

    setThothCooldown(30);
  }, [thothCooldown, errorLog, cards, biografias, matchedPairs, gameState]);

  // Poder: Ritmo de Anansi
  const handleAnansiPower = useCallback(() => {
    if (anansiCooldown > 0 || gameState !== 'playing') return;

    setIsPaused(true);
    setTimeout(() => {
      setIsPaused(false);
    }, 10000);

    setAnansiCooldown(30);
  }, [anansiCooldown, gameState]);

  // Poder: Memória de Griô
  const handleGrioPower = useCallback(() => {
    if (grioCooldown > 0 || gameState !== 'playing') return;

    setIsFlashing(true);
    setTimeout(() => {
      setIsFlashing(false);
    }, 1500);

    setGrioCooldown(30);
  }, [grioCooldown, gameState]);

  /**
   * Celebra vitória com confete e som
   */
  const celebrateVictory = () => {
    // Tocar som de vitória
    SoundManager.play('victory');
    
    // Primeiro burst - centro
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#d4af37', '#00e5ff', '#ff6b35', '#ffd700'],
      shapes: ['circle', 'square'],
      gravity: 1.2,
      decay: 0.94,
      scalar: 1.2,
      drift: 0,
      ticks: 200
    });
    
    // Segundo burst - esquerda (após 300ms)
    setTimeout(() => {
      confetti({
        particleCount: 50,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#d4af37', '#ffd700']
      });
    }, 300);
    
    // Terceiro burst - direita (após mais 300ms)
    setTimeout(() => {
      confetti({
        particleCount: 50,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#00e5ff', '#ff6b35']
      });
    }, 600);
  };

  // Fechar modal
  const handleCloseModal = () => {
    setModalData(null);
  };

  // Renderização baseada no estado
  const handleBack = () => {
    if (onNavigate) onNavigate('levels');
  };

  if (gameState === 'loading') {
    return (
      <div className="game-screen loading">
        <LoadingSplash onComplete={() => setSplashFinished(true)} />
      </div>
    );
  }

  if (gameState === 'error') {
    return (
      <div className="game-screen error">
        <div className="error-content">
          <h2>Erro ao carregar jogo</h2>
          <p>Verifique sua conexão e tente novamente.</p>
        </div>
      </div>
    );
  }

  if (gameState === 'won') {
    return (
      <div className="game-screen result">
        <div className="result-content victory">
          <h1>🏆 VITÓRIA! 🏆</h1>
          <div className="result-stats">
            <div className="stat">
              <span className="stat-label">Pontuação Final</span>
              <span className="stat-value">{score.toLocaleString()}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Tempo Restante</span>
              <span className="stat-value">{timeLeft}s</span>
            </div>
          </div>
          <button className="btn-restart" onClick={() => window.location.reload()}>
            Jogar Novamente
          </button>
        </div>
      </div>
    );
  }

  if (gameState === 'lost') {
    return (
      <div className="game-screen result">
        <div className="result-content defeat">
          <h1>⏰ TEMPO ESGOTADO</h1>
          <div className="result-stats">
            <div className="stat">
              <span className="stat-label">Pontuação</span>
              <span className="stat-value">{score.toLocaleString()}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Pares Encontrados</span>
              <span className="stat-value">{matchedPairs.length}/{totalPairs}</span>
            </div>
          </div>
          <button className="btn-restart" onClick={() => window.location.reload()}>
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="game-screen">
      <Hud
        score={score}
        timeLeft={timeLeft}
        matchedCount={matchedPairs.length}
        totalPairs={totalPairs}
        combo={combo}
        isPaused={isPaused}
        currentHighScore={currentHighScore}
        isBeatingRecord={isBeatingRecord}
        onBack={handleBack}
      />

      <GameBoard
        cards={cards}
        flippedCards={flippedCards}
        matchedPairs={matchedPairs}
        isFlashing={isFlashing}
        onCardClick={handleCardClick}
        totalPairs={totalPairs}
      />

      <PowerUpBar
        onThothPower={handleThothPower}
        onAnansiPower={handleAnansiPower}
        onGrioPower={handleGrioPower}
        thothCooldown={thothCooldown}
        anansiCooldown={anansiCooldown}
        grioCooldown={grioCooldown}
      />

      {modalData && (
        <ModalFatoRapido data={modalData} onClose={handleCloseModal} />
      )}
    </div>
  );
};

export default GameScreen;
